import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html } from 'lit';
import { of } from 'rxjs';
import * as m from '../../../../localization/out/messages.js';
import { getOrCalculateMatchingScore } from '../../../api/matching-score.js';
import type { JobPosition } from '../../../db/job-position.js';
import { MatchingScore } from '../../../db/MatchingScore.js';
import { getMatchingScoreText } from '../../../features/matching-score/utils.js';
import { translateEnum } from '../../translate-enum.js';
import { registerRouter } from './register-routes.js';

// UI-focused card matching the wireframe look (logo, details, corner selector)
export const JobCard = stateful(
  ({
    job,
    isSelected,
    onToggle,
    hideSelector = false,
    cannotApply = false,
  }: {
    job: JobPosition;
    isSelected: boolean;
    onToggle: (id: string, checked: boolean) => void;
    hideSelector?: boolean;
    cannotApply?: boolean;
  }) => {
    // read enums already present on the entity
    const de = job.languageRequirementsDE ?? undefined;
    const en = job.languageRequirementsEN ?? undefined;

    const user = useObservable(() => currentUser(), []);

    const userFitOnJob = useQuery(() => {
      if (!user?._id) return of(null);
      return getOrCalculateMatchingScore(user._id, job._id)(selectionSet(() => MatchingScore, 2));
    }, [user?._id]);

    return html` <div class="card bg-base-200 shadow-md cursor-pointer" @click=${() => onToggle(job._id, !isSelected)}>
      <div class="card-body relative p-4 flex flex-col">
        <!-- corner selector -->
        ${hideSelector
          ? html``
          : cannotApply
            ? html`
                <div class="badge badge-neutral absolute top-3 right-3" title="${m.applied_badge_title()}">
                  ${m.applied_badge()}
                </div>
              `
            : html`
                <input
                  type="checkbox"
                  class="checkbox checkbox-primary absolute top-3 right-3"
                  aria-label="select-card"
                  .checked=${isSelected}
                />
              `}

        <!-- header: logo + title -->
        <div class="flex items-center gap-4">
          <!-- company logo (if available) -->
          ${job.company?.logoID
            ? html`
                <div class="shrink-0 overflow-hidden w-14 h-14">
                  <x-image
                    class="w-full h-full"
                    .value=${job.company.logoID}
                    mode="contain"
                    alt=${job.company?.name ?? 'Company logo'}
                    disabled
                  ></x-image>
                </div>
              `
            : html`
                <div class="shrink-0 rounded-box bg-base-300 text-base-content/70 w-14 h-14 grid place-items-center">
                  <span class="text-xs">${m.company_profile_logo_placeholder()}</span>
                </div>
              `}

          <!-- title section -->
          <div class="grow flex flex-col justify-center">
            <div class="text-base font-semibold leading-tight">${job.company?.name ?? m.company()}</div>
            <div class="text-lg leading-tight">${job.title}</div>
          </div>
        </div>

        <!-- details section (full width) -->
        <div class="mt-3 text-sm grow">
          <div>${m.job_card_employment_type()}: <span class="font-medium">${job.employmentType ? translateEnum('job_form_enum', job.employmentType) : '-'}</span></div>
          <div>${m.german_level_label()}: <span class="font-medium">${de ? translateEnum('job_form_enum', de) : '-'}</span></div>
          <div>${m.english_level_label()}: <span class="font-medium">${en ? translateEnum('job_form_enum', en) : '-'}</span></div>
          <div>${m.degree()}: <span class="font-medium">${job.expectedAcademicTitle && job.expectedAcademicTitle.length > 0 ? job.expectedAcademicTitle.map(title => translateEnum('job_form_enum', title)).join(', ') : '-'}</span></div>
        </div>

        <!-- footer: matching score + details button -->
        <div class="card-actions justify-between items-center mt-3">
          <!-- matching score -->
          ${userFitOnJob.loading
            ? html`<span class="loading loading-spinner loading-sm"></span>`
            : userFitOnJob.value?.total
              ? html`
                  ${(() => {
                    const score = userFitOnJob.value?.total ?? 0;
                    const cls = score >= 70 ? 'badge-success' : score >= 40 ? 'badge-warning' : 'badge-error';
                    return html`<div class="badge ${cls}">${getMatchingScoreText(score)}</div>`;
                  })()}
                `
              : html`<div></div>`}

          <!-- details button -->
          <a
            class="btn btn-outline btn-primary btn-sm"
            @click=${(e: Event) => {
              e.stopPropagation();
              registerRouter.goTo('jobDetail', { params: { jobPositionID: job._id } });
            }}
          >
            ${m.details()}
          </a>
        </div>
      </div>
    </div>`;
  },
);
